import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from 'react';
import { jobsApi, type JobFiltersPayload } from '@/api/jobs';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';
import type { FilterOption, Job, Paging } from '@/types/types';

export interface JobsFiltersState {
    locations: FilterOption[];
    repairers: FilterOption[];
    service_types: FilterOption[];
}

export interface JobsContextValue {
    state: {
        jobs: Job[];
        activeJob: string | null;
        loading: boolean;
        loaded: boolean;
        searchTerm: string;
        sort: 'asc' | 'desc';
        filters: JobsFiltersState;
        quickFilters: Record<string, string | null>;
        dateFilter: { start: number | null; end: number | null };
        paging: Paging | null;
    };
    actions: {
        loadJobs: (params?: JobFiltersPayload) => Promise<void>;
        loadMoreJobs: (nextPageUrl: string) => Promise<void>;
        refreshJob: (id: string) => Promise<void>;
        setActiveJob: (id: string | null) => void;
        setSearchTerm: (value: string) => void;
        setSort: (value: 'asc' | 'desc') => void;
        setFilters: (filters: Partial<JobsFiltersState>) => void;
        setQuickFilters: (key: string, value: string | null) => void;
        setDateFilter: (value: { start: number | null; end: number | null }) => void;
    };
}

const emptyFilters: JobsFiltersState = {
    locations: [],
    repairers: [],
    service_types: [],
};

const JobsContext = createContext<JobsContextValue | null>(null);

export const JobsProvider = ({ children }: { children: ReactNode }) => {
    const [jobs, setJobs] = useState<Job[]>([]);
    const [activeJob, setActiveJob] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sort, setSort] = useState<'asc' | 'desc'>('desc');
    const [filters, setFiltersState] = useState<JobsFiltersState>(emptyFilters);
    const [quickFilters, setQuickFiltersState] = useState<Record<string, string | null>>({});
    const [dateFilter, setDateFilter] = useState<{ start: number | null; end: number | null }>({
        start: null,
        end: null,
    });
    const [paging, setPaging] = useState<Paging | null>(null);
    const auth = useAuth();
    const loggedIn = auth.state.loggedIn;

    useEffect(() => {
        if (!loggedIn) {
            setJobs([]);
            setActiveJob(null);
            setLoading(false);
            setLoaded(false);
            setSearchTerm('');
            setSort('desc');
            setFiltersState(emptyFilters);
            setQuickFiltersState({});
            setDateFilter({ start: null, end: null });
            setPaging(null);
        }
    }, [loggedIn]);

    const loadJobs = useCallback(async (params: JobFiltersPayload = {}) => {
        setLoading(true);
        setJobs([]);
        try {
            const res = await jobsApi.list(params);
            const payload = res.data?.payload ?? [];
            setJobs(payload);
            setPaging(res.data?.paging ?? null);
            setLoaded(true);
            setActiveJob(payload[0]?.id ?? null);
        } catch {
            setJobs([]);
            setLoaded(true);
            setActiveJob(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadMoreJobs = useCallback(async (nextPageUrl: string) => {
        try {
            const res = await jobsApi.listByNextPageUrl(nextPageUrl);
            const more = res.data?.payload ?? [];
            setJobs((prev) => [...prev, ...more]);
            setPaging(res.data?.paging ?? null);
        } catch {}
    }, []);

    const refreshJob = useCallback(async (id: string) => {
        try {
            const res = await jobsApi.byId(id);
            const updated = res.data?.payload;
            if (updated) {
                setJobs((prev) => prev.map((j) => (j.id === id ? updated : j)));
            }
        } catch {}
    }, []);

    const setFilters = useCallback((next: Partial<JobsFiltersState>) => {
        setFiltersState((prev) => ({ ...prev, ...next }));
    }, []);

    const setQuickFilters = useCallback((key: string, value: string | null) => {
        setQuickFiltersState((prev) => ({ ...prev, [key]: value }));
    }, []);

    return (
        <JobsContext.Provider
            value={{
                state: {
                    jobs,
                    activeJob,
                    loading,
                    loaded,
                    searchTerm,
                    sort,
                    filters,
                    quickFilters,
                    dateFilter,
                    paging,
                },
                actions: {
                    loadJobs,
                    loadMoreJobs,
                    refreshJob,
                    setActiveJob,
                    setSearchTerm,
                    setSort,
                    setFilters,
                    setQuickFilters,
                    setDateFilter,
                },
            }}
        >
            {children}
        </JobsContext.Provider>
    );
};

export const useJobs = (): JobsContextValue => {
    const ctx = useContext(JobsContext);
    if (!ctx) throw new Error('useJobs must be used inside JobsProvider');
    return ctx;
};
